import type {
    AreaIndicador,
    Agrupacion,
    CifrasIndicador,
    Comparacion,
    ConsultaCifras,
    Dimension,
    EstadoExportacion,
    Exportacion,
    Indicador,
    ModoComparacion,
    NuevaExportacion,
    PuntoIndicador,
    SerieIndicador,
    UnidadIndicador,
} from '../../api/ops/indicadores';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Reglas sin estilos de «Indicadores» (7.2) y «Exportaciones» (7.3): periodos y su límite de 25 meses, agrupar el
 * catálogo por área, escribir cada cifra según su unidad, los días incompletos (solo `history_missing` y
 * `rebuild_pending`, G425) y el formulario de «Nueva exportación». **Ningún código de indicador**: todos salen de
 * `listIndicators` (corrección 7.1·7.2); aquí solo hay áreas, unidades y dimensiones, que son enumerados del contrato.
 */

// ---------- Fechas `AAAA-MM-DD` (fecha local del negocio, sin zona) ----------

const MES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const MES_LARGO = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIA_MS = 86_400_000;

function aUtc(fecha: string): number {
    const [a, m, d] = fecha.split('-').map(Number);
    return Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function deUtc(t: number): string {
    return new Date(t).toISOString().slice(0, 10);
}

export function sumarDias(fecha: string, dias: number): string {
    return deUtc(aUtc(fecha) + dias * DIA_MS);
}

/** Suma meses de calendario (el 31 de un mes corto se queda en su último día). */
export function sumarMeses(fecha: string, meses: number): string {
    const f = new Date(aUtc(fecha));
    const dia = f.getUTCDate();
    const destino = new Date(Date.UTC(f.getUTCFullYear(), f.getUTCMonth() + meses, 1));
    const ultimo = new Date(Date.UTC(destino.getUTCFullYear(), destino.getUTCMonth() + 1, 0)).getUTCDate();
    destino.setUTCDate(Math.min(dia, ultimo));
    return deUtc(destino.getTime());
}

/** Días del periodo, los dos incluidos. */
export function diasEntre(desde: string, hasta: string): number {
    return Math.round((aUtc(hasta) - aUtc(desde)) / DIA_MS) + 1;
}

const esFecha = (f: string) => /^\d{4}-\d{2}-\d{2}$/.test(f);

/** «15 sept» (con el año si se pide). */
export function diaCorto(fecha: string, conAnio = false): string {
    const f = new Date(aUtc(fecha));
    const base = `${String(f.getUTCDate())} ${MES[f.getUTCMonth()] ?? ''}`;
    return conAnio ? `${base} ${String(f.getUTCFullYear())}` : base;
}

/** «Del 15 sept al 14 oct 2026», «Del 1 al 14 oct 2026» o «Del 3 dic 2025 al 2 ene 2026», como el diseño. */
export function textoRango(desde: string, hasta: string): string {
    const a = new Date(aUtc(desde));
    const b = new Date(aUtc(hasta));
    if (a.getUTCFullYear() !== b.getUTCFullYear()) {
        return `Del ${diaCorto(desde, true)} al ${diaCorto(hasta, true)}`;
    }
    if (a.getUTCMonth() === b.getUTCMonth()) {
        return `Del ${String(a.getUTCDate())} al ${diaCorto(hasta, true)}`;
    }
    return `Del ${diaCorto(desde)} al ${diaCorto(hasta, true)}`;
}

/** El periodo de una exportación: «Septiembre de 2026» si es un mes entero; si no, el rango. */
export function textoPeriodoExportacion(desde: string, hasta: string): string {
    const a = new Date(aUtc(desde));
    const finDeMes = sumarDias(sumarMeses(`${desde.slice(0, 7)}-01`, 1), -1);
    if (a.getUTCDate() === 1 && hasta === finDeMes) {
        const mes = MES_LARGO[a.getUTCMonth()] ?? '';
        return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} de ${String(a.getUTCFullYear())}`;
    }
    return textoRango(desde, hasta);
}

/** Etiqueta de un punto de la serie: «15 sept», «Semana del 15 sept» o «Septiembre 2026». */
export function etiquetaPunto(inicio: string, agrupacion: Agrupacion): string {
    if (agrupacion === 'month') {
        const f = new Date(aUtc(inicio));
        const mes = MES_LARGO[f.getUTCMonth()] ?? '';
        return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${String(f.getUTCFullYear())}`;
    }
    if (agrupacion === 'week') {
        return `Semana del ${diaCorto(inicio)}`;
    }
    return diaCorto(inicio);
}

// ---------- Periodos y su límite (ficha §9: 25 meses) ----------

export type Periodo = '7d' | '30d' | 'mes' | 'mes_ant' | '12m' | 'fechas';

export const PERIODOS: readonly { valor: Periodo; texto: string }[] = [
    { valor: '7d', texto: 'Últimos 7 días' },
    { valor: '30d', texto: 'Últimos 30 días' },
    { valor: 'mes', texto: 'Este mes' },
    { valor: 'mes_ant', texto: 'Mes pasado' },
    { valor: '12m', texto: 'Últimos 12 meses' },
    { valor: 'fechas', texto: 'Elegir fechas' },
];

export const COMPARACIONES: readonly { valor: ModoComparacion; texto: string }[] = [
    { valor: 'none', texto: 'Sin comparar' },
    { valor: 'previous_period', texto: 'El periodo anterior' },
    { valor: 'previous_year', texto: 'El año pasado' },
];

export const AGRUPACIONES: readonly { valor: Exclude<Agrupacion, 'hour'>; texto: string; por: string }[] = [
    { valor: 'day', texto: 'Día', por: 'Por día' },
    { valor: 'week', texto: 'Semana', por: 'Por semana' },
    { valor: 'month', texto: 'Mes', por: 'Por mes' },
];

export interface Rango {
    desde: string;
    hasta: string;
}

/** Las fechas de un periodo con `hoy` (fecha local). «Elegir fechas» devuelve las elegidas tal cual. */
export function rangoDePeriodo(periodo: Periodo, hoy: string, elegidas: Rango = { desde: '', hasta: '' }): Rango {
    const primeroDeMes = `${hoy.slice(0, 7)}-01`;
    switch (periodo) {
        case '7d':
            return { desde: sumarDias(hoy, -6), hasta: hoy };
        case '30d':
            return { desde: sumarDias(hoy, -29), hasta: hoy };
        case 'mes':
            return { desde: primeroDeMes, hasta: hoy };
        case 'mes_ant':
            return { desde: sumarMeses(primeroDeMes, -1), hasta: sumarDias(primeroDeMes, -1) };
        case '12m':
            return { desde: sumarMeses(primeroDeMes, -11), hasta: hoy };
        case 'fechas':
            return elegidas;
    }
}

/** ¿`hasta` cae como mucho 25 meses después de `desde`? (lo mismo que comprueba la API, `validation_failed`). */
export function dentroDe25Meses(desde: string, hasta: string): boolean {
    return hasta <= sumarMeses(desde, 25);
}

/** El error de un periodo elegido a mano, o null. `cola` = « por archivo» en la exportación. */
export function errorDeRango({ desde, hasta }: Rango, cola = ''): string | null {
    if (!esFecha(desde) || !esFecha(hasta)) {
        return 'Elige las dos fechas.';
    }
    if (hasta < desde) {
        return 'La fecha final va después de la inicial.';
    }
    if (!dentroDe25Meses(desde, hasta)) {
        return `Como mucho 25 meses${cola}.`;
    }
    return null;
}

/** Agrupaciones que tienen sentido para el periodo (como el diseño: sin días en más de 400, ni semanas en menos de 14…). */
export function agrupacionesPara(dias: number): readonly (typeof AGRUPACIONES)[number][] {
    return AGRUPACIONES.filter((g) => !(g.valor === 'day' && dias > 400) && !(g.valor === 'month' && dias < 28) && !(g.valor === 'week' && dias < 14));
}

/** La agrupación por defecto: por mes en periodos largos (más de 92 días), por día en el resto. */
export function agrupacionPorDefecto(dias: number): Exclude<Agrupacion, 'hour'> {
    return dias > 92 ? 'month' : 'day';
}

/** La agrupación elegida si vale para el periodo; si no, la de por defecto. */
export function agrupacionValida(elegida: Exclude<Agrupacion, 'hour'> | null, dias: number): Exclude<Agrupacion, 'hour'> {
    return elegida !== null && agrupacionesPara(dias).some((g) => g.valor === elegida) ? elegida : agrupacionPorDefecto(dias);
}

/** Los filtros de 7.2 (la agrupación null = la de por defecto para el periodo). */
export interface Filtro {
    periodo: Periodo;
    fechas: Rango;
    sede: string;
    comparar: ModoComparacion;
    agrupacion: 'day' | 'week' | 'month' | null;
}

/** La consulta de `queryIndicator` con los filtros; null si las fechas elegidas no valen. */
export function consultaDe(f: Filtro, hoy: string, extra: Partial<ConsultaCifras> = {}): ConsultaCifras | null {
    const r = rangoDePeriodo(f.periodo, hoy, f.fechas);
    if (errorDeRango(r) !== null) {
        return null;
    }
    return { from: r.desde, to: r.hasta, granularity: agrupacionValida(f.agrupacion, diasEntre(r.desde, r.hasta)), compare: f.comparar, ...(f.sede === '' ? {} : { location_id: f.sede }), ...extra };
}

// ---------- Catálogo por áreas ----------

/**
 * Nombre e icono de cada área (`IndicatorArea`), en el orden del diseño. `pases` (G801: llegadas y asistencia) como en 7.2,
 * «Pases» con el QR; `rrpp`, `campanas` y `web` no tienen nombre en el diseño (pregunta abierta): llevan el de su módulo. Un área que el panel aún no conoce sale con su
 * clave y un icono genérico (G801: valor nuevo compatible).
 */
export const AREAS: Readonly<Record<string, { nombre: string; icono: NombreIcono }>> = {
    reservas: { nombre: 'Reservas', icono: 'book-open' },
    citas: { nombre: 'Citas', icono: 'calendar-days' },
    entradas: { nombre: 'Eventos y entradas', icono: 'ticket' },
    pases: { nombre: 'Pases', icono: 'qr-code' },
    rrpp: { nombre: 'Listas y RRPP', icono: 'list-checks' },
    pagos: { nombre: 'Pagos', icono: 'credit-card' },
    clientes: { nombre: 'Clientes', icono: 'users' },
    campanas: { nombre: 'Campañas', icono: 'send' },
    web: { nombre: 'Web', icono: 'globe' },
    canales: { nombre: 'Canales y asistente', icono: 'message-circle' },
    entregas: { nombre: 'Avisos y recordatorios', icono: 'bell' },
    consumo: { nombre: 'Consumo', icono: 'gauge' },
    plataforma: { nombre: 'Plataforma', icono: 'building-2' },
};

export function area(clave: string): { nombre: string; icono: NombreIcono } {
    return AREAS[clave] ?? { nombre: clave.charAt(0).toUpperCase() + clave.slice(1), icono: 'chart-line' };
}

export interface GrupoArea {
    area: AreaIndicador;
    nombre: string;
    icono: NombreIcono;
    indicadores: Indicador[];
}

/** El catálogo por área, en el orden de `AREAS` (las desconocidas al final) y, dentro de cada una, en el de la API. */
export function agruparPorArea(indicadores: readonly Indicador[]): GrupoArea[] {
    const orden = Object.keys(AREAS);
    const grupos = new Map<string, Indicador[]>();
    for (const i of indicadores) {
        grupos.set(i.area, [...(grupos.get(i.area) ?? []), i]);
    }
    const pos = (a: string) => (orden.includes(a) ? orden.indexOf(a) : orden.length);
    return [...grupos.entries()]
        .sort(([a], [b]) => pos(a) - pos(b))
        .map(([clave, lista]) => ({ area: clave as AreaIndicador, ...area(clave), indicadores: lista }));
}

/**
 * Lo que ve quien solo tiene «lo suyo» (Profesional, RRPP): solo los indicadores que declaran `own_scope`. Quien lo ve todo,
 * el catálogo entero tal como llega.
 */
export function visiblesConAmbito(indicadores: readonly Indicador[], soloLoSuyo: boolean): Indicador[] {
    return soloLoSuyo ? indicadores.filter((i) => i.own_scope === true) : [...indicadores];
}

/** Cuántos enseña cada grupo antes de «Ver los N de …» (diseño: 2–3 a la vista). */
export const A_LA_VISTA = 3;

export const DIMENSIONES: Readonly<Record<Dimension, string>> = {
    location: 'Sede',
    zone: 'Zona',
    resource: 'Recurso',
    slot: 'Franja',
    professional: 'Profesional',
    service: 'Servicio',
    event: 'Evento',
    ticket_type: 'Tipo de entrada',
    guest_list: 'Lista',
    promoter: 'RRPP',
    origin: 'Origen',
    widget: 'Widget',
    channel: 'Canal',
    concept: 'Concepto',
    payment_method: 'Forma de pago',
    campaign: 'Campaña',
    message_type: 'Tipo de mensaje',
};

export const nombreDimension = (d: string): string => (DIMENSIONES as Record<string, string | undefined>)[d] ?? d;

export const LECTURA: Readonly<Record<string, string>> = {
    'analitica.operativos.ver': 'Operativo, sin importes',
    'analitica.economicos.ver': 'Con importes',
    'analitica.comerciales.ver': 'Comercial, agregado',
    'analitica.consumo.ver': 'Consumo del plan',
    'plataforma.indicadores.ver': 'Solo Reservaria',
};

export const PAPEL_EVENTO: Readonly<Record<string, string>> = { adds: 'suma', subtracts: 'resta', capacity: 'capacidad', attribute: 'atributo' };

export const BASE_FECHA: Readonly<Record<string, string>> = {
    service: 'Para el que es (la reserva del sábado cuenta el sábado)',
    occurred: 'En que pasa (la venta, el cobro, la llegada)',
};

// ---------- Cifras por unidad ----------

function numero(n: number, decimales: number): string {
    return n
        .toLocaleString('es-ES', { minimumFractionDigits: decimales, maximumFractionDigits: decimales, useGrouping: 'always' })
        .replace('-', '−');
}

export interface CifraEscrita {
    /** «1.366», «67.772», «62,7». */
    cifra: string;
    /** «€», «%», «min», «h», «días» o vacío en los recuentos. */
    unidad: string;
}

function simbolo(moneda: string): string {
    try {
        return new Intl.NumberFormat('es-ES', { style: 'currency', currency: moneda }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? moneda;
    } catch {
        return moneda;
    }
}

/**
 * Un punto escrito según su unidad: `count` entero, `money` en su moneda (céntimos de la API; sin decimales en grande,
 * con dos en la tabla), `percent` y `days` con un decimal, `minutes` y `hours`. Sin cifra, «—».
 */
export function escribir(unidad: UnidadIndicador | undefined, punto: Pick<PuntoIndicador, 'value' | 'money'> | null | undefined, largo = false): CifraEscrita {
    if (punto == null) {
        return { cifra: '—', unidad: '' };
    }
    if (unidad === 'money') {
        if (punto.money == null) {
            return { cifra: '—', unidad: '' };
        }
        return { cifra: numero(punto.money.amount / 100, largo ? 2 : 0), unidad: simbolo(punto.money.currency) };
    }
    const v = punto.value;
    if (v == null) {
        return { cifra: '—', unidad: '' };
    }
    switch (unidad) {
        case 'percent':
            return { cifra: numero(v, 1), unidad: '%' };
        case 'days':
            return { cifra: numero(v, 1), unidad: 'días' };
        case 'hours':
            return { cifra: numero(v, 1), unidad: 'h' };
        case 'minutes':
            return { cifra: numero(v, 0), unidad: 'min' };
        default:
            return { cifra: numero(v, 0), unidad: '' };
    }
}

/** La cifra en una línea: «1.366», «67.772 €», «62,7 %». */
export function texto(unidad: UnidadIndicador | undefined, punto: Pick<PuntoIndicador, 'value' | 'money'> | null | undefined, largo = false): string {
    const e = escribir(unidad, punto, largo);
    return e.unidad === '' ? e.cifra : `${e.cifra} ${e.unidad}`;
}

/** El valor para dibujar (los importes en unidades de su moneda); null sin cifra. */
export function valorDibujable(punto: Pick<PuntoIndicador, 'value' | 'money'>): number | null {
    if (punto.money != null) {
        return punto.money.amount / 100;
    }
    return punto.value ?? null;
}

/** «1.152 de 1.812» en los ratios (`percent`), con el numerador y el denominador de la API. */
export function deCada(punto: Pick<PuntoIndicador, 'numerator' | 'denominator'>): string | null {
    if (punto.numerator == null || punto.denominator == null) {
        return null;
    }
    return `${numero(punto.numerator, 0)} de ${numero(punto.denominator, 0)}`;
}

export interface Cambio {
    texto: string;
    sentido: 'sube' | 'baja' | 'igual';
}

/**
 * El cambio frente a la comparación, tal como lo da la API (el panel no lo calcula): en los porcentajes, puntos
 * (`change_absolute`, «+0,2 p. p.»); en el resto, `change_percent` («+7,6 %»). Null sin comparación o si la API no lo da
 * (periodo anterior a cero o incompleto). El color no dice si es bueno o malo (no lo da la API: pregunta abierta).
 */
export function cambioDe(unidad: UnidadIndicador | undefined, comparacion: Comparacion | null | undefined, serie: Pick<SerieIndicador, 'dimension_value' | 'currency'> | null = null): Cambio | null {
    if (comparacion == null || comparacion.mode === 'none') {
        return null;
    }
    const s = comparacion.series.find((x) => (x.dimension_value ?? null) === (serie?.dimension_value ?? null) && (serie?.currency == null || x.currency === serie.currency)) ?? comparacion.series[0];
    if (s === undefined) {
        return null;
    }
    const enPuntos = unidad === 'percent';
    const v = enPuntos ? s.change_absolute : s.change_percent;
    if (v == null) {
        return null;
    }
    const abs = Math.abs(v);
    const cifra = numero(abs, enPuntos || abs < 10 ? 1 : 0);
    const igual = enPuntos ? abs < 0.05 : abs < 0.5;
    return { texto: `${v < 0 ? '−' : '+'}${cifra} ${enPuntos ? 'p. p.' : '%'}`, sentido: igual ? 'igual' : v < 0 ? 'baja' : 'sube' };
}

export function textoComparacion(modo: ModoComparacion): string {
    return modo === 'previous_year' ? 'el mismo periodo del año pasado' : 'el periodo anterior';
}

// ---------- Días incompletos (G425) ----------

export type MotivoMarcado = 'history_missing' | 'rebuild_pending';

/**
 * Por qué un punto queda «sin datos completos»: solo `history_missing` y `rebuild_pending` (G425). Un salto de secuencia
 * (`sequence_gap`) no lo marca: la cifra es provisional y se corrige sola.
 */
export function motivoIncompleto(punto: Pick<PuntoIndicador, 'complete' | 'incomplete_reason'>): MotivoMarcado | null {
    if (punto.complete) {
        return null;
    }
    return punto.incomplete_reason === 'history_missing' || punto.incomplete_reason === 'rebuild_pending' ? punto.incomplete_reason : null;
}

/** La nota bajo el gráfico para el primer punto incompleto (textos a validar con el equipo: pregunta abierta). */
export function notaIncompleto(motivo: MotivoMarcado, etiqueta: string): string {
    const e = `${etiqueta.charAt(0).toUpperCase()}${etiqueta.slice(1)}`;
    return motivo === 'history_missing'
        ? `${e}: es anterior al archivo de eventos y queda sin datos completos, nunca inventado.`
        : `${e}: este indicador se está recalculando y queda sin datos completos hasta que termine.`;
}

// ---------- Exportaciones (7.3) ----------

export const ESTADOS_EXPORTACION: Readonly<Record<EstadoExportacion, { texto: string; chip: EstadoChip }>> = {
    preparing: { texto: 'Preparando', chip: 'pending' },
    ready: { texto: 'Lista', chip: 'confirmed' },
    expired: { texto: 'Caducada', chip: 'problem' },
    failed: { texto: 'Fallida', chip: 'problem' },
};

export const FILTROS_EXPORTACION: readonly { valor: EstadoExportacion | null; texto: string }[] = [
    { valor: null, texto: 'Todas' },
    { valor: 'ready', texto: 'Listas' },
    { valor: 'preparing', texto: 'Preparando' },
    { valor: 'expired', texto: 'Caducadas' },
    { valor: 'failed', texto: 'Fallidas' },
];

function diasQueQuedan(iso: string, ahora: Date): number {
    return Math.ceil((new Date(iso).getTime() - ahora.getTime()) / DIA_MS);
}

/** El chip y la línea de debajo de cada exportación, como el diseño («Caduca pronto» si queda un día o menos). */
export function estadoExportacion(x: Pick<Exportacion, 'status' | 'expires_at'>, ahora: Date = new Date()): { chip: string; tono: EstadoChip; linea: string } {
    const base = ESTADOS_EXPORTACION[x.status];
    switch (x.status) {
        case 'ready': {
            if (x.expires_at == null) {
                return { chip: base.texto, tono: base.chip, linea: 'El enlace caduca a los 7 días' };
            }
            const quedan = diasQueQuedan(x.expires_at, ahora);
            return quedan <= 1
                ? { chip: 'Caduca pronto', tono: 'pending', linea: 'El enlace caduca mañana' }
                : { chip: base.texto, tono: base.chip, linea: `El enlace caduca en ${String(quedan)} días` };
        }
        case 'preparing':
            return { chip: base.texto, tono: base.chip, linea: 'Te avisamos en la campana' };
        case 'expired':
            return { chip: base.texto, tono: base.chip, linea: x.expires_at == null ? 'El enlace caducó' : `El enlace caducó el ${diaCorto(new Date(x.expires_at).toISOString().slice(0, 10))}` };
        case 'failed':
            return { chip: base.texto, tono: base.chip, linea: 'No se pudo preparar' };
    }
}

/** «Reservas y 3 más» y debajo los demás nombres (o el formato si es uno). Un código que ya no está en el catálogo sale tal cual. */
export function tituloExportacion(x: Pick<Exportacion, 'indicators' | 'format'>, nombreDe: (codigo: string) => string): { titulo: string; sub: string } {
    const nombres = x.indicators.map((i) => nombreDe(i.code));
    const formato = x.format === 'csv' ? 'CSV' : 'Excel';
    if (nombres.length <= 1) {
        return { titulo: nombres[0] ?? '', sub: formato };
    }
    return { titulo: `${nombres[0] ?? ''} y ${String(nombres.length - 1)} más`, sub: nombres.slice(1).join(', ') };
}

/** «Por día · Todas las sedes · Excel». */
export function detalleExportacion(x: Pick<Exportacion, 'granularity' | 'location_id' | 'format'>, nombreSede: (id: string) => string): string {
    const por = AGRUPACIONES.find((g) => g.valor === (x.granularity ?? 'day'))?.por ?? 'Por día';
    return [por, x.location_id == null ? 'Todas las sedes' : nombreSede(x.location_id), x.format === 'csv' ? 'CSV' : 'Excel'].join(' · ');
}

export const MAX_INDICADORES = 20;

export interface FormExportacion {
    periodo: Periodo;
    fechas: Rango;
    agrupacion: Exclude<Agrupacion, 'hour'>;
    sede: string;
    desglose: Dimension | '';
    comparar: ModoComparacion;
    formato: 'csv' | 'xlsx';
    elegidos: readonly string[];
}

export function formularioInicial(elegidos: readonly string[] = []): FormExportacion {
    return { periodo: '30d', fechas: { desde: '', hasta: '' }, agrupacion: 'day', sede: '', desglose: '', comparar: 'previous_period', formato: 'xlsx', elegidos };
}

/** «Pedir otra vez»: el mismo formulario con lo que llevaba la exportación (sus fechas exactas). */
export function formularioDe(x: Exportacion): FormExportacion {
    return {
        periodo: 'fechas',
        fechas: { desde: x.from, hasta: x.to },
        agrupacion: x.granularity ?? 'day',
        sede: x.location_id ?? '',
        desglose: x.breakdown ?? '',
        comparar: x.compare ?? 'none',
        formato: x.format,
        elegidos: x.indicators.map((i) => i.code),
    };
}

export function erroresExportacion(f: FormExportacion, hoy: string): { elegidos?: string; fechas?: string } {
    const e: { elegidos?: string; fechas?: string } = {};
    if (f.elegidos.length === 0) {
        e.elegidos = 'Elige al menos un indicador.';
    } else if (f.elegidos.length > MAX_INDICADORES) {
        e.elegidos = `Como mucho 20 indicadores por archivo. Llevas ${String(f.elegidos.length)}.`;
    }
    const error = errorDeRango(rangoDePeriodo(f.periodo, hoy, f.fechas), ' por archivo');
    if (error !== null) {
        e.fechas = error;
    }
    return e;
}

/** Las dimensiones que se pueden desglosar en la exportación: las que declaran todos los indicadores elegidos. */
export function desglosesComunes(elegidos: readonly Indicador[]): Dimension[] {
    const [primero, ...resto] = elegidos;
    if (primero === undefined) {
        return [];
    }
    return primero.dimensions.filter((d) => resto.every((i) => i.dimensions.includes(d)));
}

export function cuerpoExportacion(f: FormExportacion, hoy: string): NuevaExportacion {
    const { desde, hasta } = rangoDePeriodo(f.periodo, hoy, f.fechas);
    return {
        indicators: [...f.elegidos],
        from: desde,
        to: hasta,
        granularity: f.agrupacion,
        ...(f.sede === '' ? {} : { location_id: f.sede }),
        ...(f.desglose === '' ? {} : { breakdown: f.desglose }),
        compare: f.comparar,
        format: f.formato,
    };
}

// ---------- Textos de la «?» (diseño, HELP) ----------

/** Línea bajo el título (0.1, PAGES de `diseno/plataforma/0.1-marco-del-panel.html`). */
export const SUBTITULO_INDICADORES = 'Las cifras de tu negocio, por módulo y periodo.';

export const AYUDA_INDICADORES = [
    { icono: 'chart-line', titulo: 'Indicadores', texto: 'Las cifras de tu negocio, por módulo. Solo ves las de los bloques que tienes y las que permite tu rol.' },
    { icono: 'calendar', titulo: 'Periodo y comparación', texto: 'Elige el periodo y compáralo con el anterior o con el mismo del año pasado. Los días se cortan con la hora de tu negocio.' },
    { icono: 'circle-alert', titulo: 'Sin datos completos', texto: 'Si falta algún dato de un día, lo marcamos en vez de suponer. Se completa solo cuando llega.' },
] as const;

export const AYUDA_EXPORTACIONES = [
    { icono: 'download', titulo: 'Exportar', texto: 'CSV o Excel con las cifras, sus filtros, la zona horaria y la versión de cada indicador. Hasta 20 indicadores y 25 meses.' },
    { icono: 'clock', titulo: 'Enlace de 7 días', texto: 'Las grandes se preparan en segundo plano y te avisamos en la campana. El enlace caduca a los 7 días.' },
    { icono: 'shield', titulo: 'Solo cifras', texto: 'Nunca datos de clientes: esos se exportan desde Clientes. Ves solo las exportaciones que has pedido tú.' },
] as const;

// ---------- Color del cambio (pregunta 324) ----------

export type Tono = 'bueno' | 'malo' | 'neutro';

/**
 * Si que suba es bueno: `higher_is_better` del indicador (G826, lo declara su manifiesto). Sin él (un catálogo que no
 * llegó), null: el panel no adivina si subir es bueno (unos no-shows que suben no son una buena noticia).
 */
export function mejorSiSube(indicador: Pick<Indicador, 'higher_is_better'> | null | undefined): boolean | null {
    const v = indicador?.higher_is_better;
    return typeof v === 'boolean' ? v : null;
}

/** Verde si va bien, rojo si va mal y gris si no se sabe o no cambia (7.2, «Lo importante»). */
export function tonoDelCambio(sentido: Cambio['sentido'], mejor: boolean | null): Tono {
    if (sentido === 'igual' || mejor === null) {
        return 'neutro';
    }
    return (sentido === 'sube') === mejor ? 'bueno' : 'malo';
}

// ---------- «Lo importante» (7.2) ----------

/** Cuántas cifras clave salen arriba, en grande (José, 16 oct: 3 o 4). */
export const IMPORTANTES = 4;

/**
 * Las cifras clave de 7.2: las de la tarjeta «Periodo» de `getDashboard`, en el orden que declaran los manifiestos
 * (`tarjetas.period`), que la persona puede leer y que están en el catálogo que ve. Sin códigos escritos en el panel.
 */
export function importantes(codigosPeriodo: readonly string[], visibles: readonly Indicador[]): Indicador[] {
    return codigosPeriodo.flatMap((c) => visibles.filter((i) => i.code === c)).slice(0, IMPORTANTES);
}

/** La frase llana bajo la cifra: «Vas un 7,6 % por encima del periodo anterior.»; sin comparación, nada. */
export function fraseCambio(cambio: Cambio | null, modo: ModoComparacion, incompleto: boolean): string {
    const falta = incompleto ? ' Falta un día por completar.' : '';
    if (cambio === null || modo === 'none') {
        return falta.trim();
    }
    const frente = textoComparacion(modo).replace(/^el /, 'del ');
    if (cambio.sentido === 'igual') {
        return `Igual que ${textoComparacion(modo)}.${falta}`;
    }
    const cuanto = cambio.texto.slice(1).replace(' p. p.', ' puntos');
    return `Vas ${cuanto.endsWith('puntos') ? '' : 'un '}${cuanto} por ${cambio.sentido === 'sube' ? 'encima' : 'debajo'} ${frente}.${falta}`;
}

// ---------- Mapa de calor día × hora (7.2) ----------

/** Como mucho 31 días con `granularity=hour` (contrato): el calor mira las 4 últimas semanas del periodo. */
export const DIAS_CALOR = 28;

/** El indicador del calor de un área: el primero que se puede leer por hora (`supports_hourly`) y es un recuento. */
export function indicadorDeCalor(indicadores: readonly Indicador[]): Indicador | null {
    return indicadores.find((i) => i.supports_hourly === true && i.unit === 'count') ?? null;
}

/** Las fechas del calor: el final del periodo y, como mucho, los 28 días anteriores. */
export function rangoDeCalor(desde: string, hasta: string): Rango & { recortado: boolean } {
    const inicio = sumarDias(hasta, -(DIAS_CALOR - 1));
    return inicio > desde ? { desde: inicio, hasta, recortado: true } : { desde, hasta, recortado: false };
}

export const DIAS_SEMANA: readonly { corto: string; largo: string }[] = [
    { corto: 'L', largo: 'Lunes' },
    { corto: 'M', largo: 'Martes' },
    { corto: 'X', largo: 'Miércoles' },
    { corto: 'J', largo: 'Jueves' },
    { corto: 'V', largo: 'Viernes' },
    { corto: 'S', largo: 'Sábado' },
    { corto: 'D', largo: 'Domingo' },
];

export interface Calor {
    /** Horas con algo, en el orden del día del negocio (si abre de noche, 21 · 22 · 23 · 0 · 1). */
    horas: number[];
    /** Una fila por día de la semana (lunes primero): la media de cada hora en ese día; null si nunca hubo nada. */
    filas: { corto: string; largo: string; valores: (number | null)[] }[];
    maximo: number;
}

/** Lunes = 0 … domingo = 6 de una fecha local. */
function diaSemana(fecha: string): number {
    const [a, m, d] = fecha.split('-').map(Number);
    return (new Date(Date.UTC(a ?? 2000, (m ?? 1) - 1, d ?? 1)).getUTCDay() + 6) % 7;
}

/** Ordena las horas empezando después del hueco más largo sin nada (un local de noche empieza a las 21, no a las 0). */
export function enOrdenDelDia(horas: readonly number[]): number[] {
    const h = [...new Set(horas)].sort((a, b) => a - b);
    if (h.length < 2) {
        return h;
    }
    let corte = 0;
    let hueco = (h[0] ?? 0) + 24 - (h[h.length - 1] ?? 0);
    for (let i = 1; i < h.length; i += 1) {
        const salto = (h[i] ?? 0) - (h[i - 1] ?? 0);
        if (salto > hueco) {
            hueco = salto;
            corte = i;
        }
    }
    const desde = h[corte] ?? 0;
    const hasta = h[(corte + h.length - 1) % h.length] ?? 23;
    const todas: number[] = [];
    for (let x = desde; ; x = (x + 1) % 24) {
        todas.push(x);
        if (x === hasta || todas.length >= 24) {
            break;
        }
    }
    return todas;
}

/**
 * El calor día × hora de una serie por hora (`granularity=hour`): para cada día de la semana y hora, la media de los
 * días de ese tipo que hay en el periodo. Solo la serie del total (sin desglose).
 */
export function calorDe(cifras: Pick<CifrasIndicador, 'series' | 'from' | 'to'> | null): Calor | null {
    const serie = cifras?.series.find((s) => (s.dimension_value ?? null) === null);
    if (cifras == null || serie === undefined) {
        return null;
    }
    const cuantos = Array.from({ length: 7 }, () => 0);
    for (let f = cifras.from; f <= cifras.to; f = sumarDias(f, 1)) {
        cuantos[diaSemana(f)] = (cuantos[diaSemana(f)] ?? 0) + 1;
    }
    const suma = new Map<string, number>();
    for (const p of serie.points) {
        if (p.hour == null || p.value == null || p.value <= 0) {
            continue;
        }
        const k = `${String(diaSemana(p.period_start))}|${String(p.hour)}`;
        suma.set(k, (suma.get(k) ?? 0) + p.value);
    }
    if (suma.size === 0) {
        return null;
    }
    const horas = enOrdenDelDia([...suma.keys()].map((k) => Number(k.split('|')[1])));
    let maximo = 0;
    const filas = DIAS_SEMANA.map((d, i) => ({
        ...d,
        valores: horas.map((h) => {
            const v = suma.get(`${String(i)}|${String(h)}`);
            if (v === undefined) {
                return null;
            }
            const media = v / Math.max(1, cuantos[i] ?? 1);
            maximo = Math.max(maximo, media);
            return media;
        }),
    }));
    return { horas, filas, maximo };
}

/** De 0 a 4: lo claro (poco) a lo oscuro (mucho), sobre el máximo del mapa. */
export function nivelCalor(valor: number | null, maximo: number): number {
    if (valor === null || maximo <= 0) {
        return 0;
    }
    return Math.max(1, Math.min(4, Math.ceil((valor / maximo) * 4)));
}
